import { useCallback, useEffect, useMemo, useState } from "react";
import createDOMPurify from "@/vendor/purify.es.mjs";
import { marked } from "@/vendor/marked.esm.js";
import { apiRequest } from "@/utils/apiClient";
import { PageHeader, Button } from "@/components/ui";
import { Icon } from "@/components/IconSprite";
import { formatDistanceToNow, format } from "date-fns";

interface Session {
  session_id: string;
  user_id: string;
  user_role: string;
  message_count: number;
  first_message_at: string;
  last_message_at: string;
  first_query: string;
}

interface Message {
  message_id: string;
  user_id: string;
  session_id: string;
  time: string;
  query: string;
  response: string;
  user_role: string;
  token_used: number;
  response_time_ms: number;
}

interface SessionsResponse {
  sessions: Session[];
  total: number;
  limit: number;
  offset: number;
}

interface MessagesResponse {
  session_id: string;
  messages: Message[];
  total: number;
}

const ROLE_RB: Record<string, string> = {
  admin: "admin",
  business_owner: "bo",
  guest: "guest",
  preferred_member: "pm",
  product_center: "pc",
  branch_operator: "bop",
};

const RoleBadge = ({ role }: { role: string }) => (
  <span className={`rb ${ROLE_RB[role] || ROLE_RB.guest}`}>{role.replace(/_/g, " ")}</span>
);

const Conversations = () => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const dompurify = useMemo(() => {
    if (typeof window === "undefined") return null;
    return createDOMPurify(window);
  }, []);

  const renderMarkdown = (value: string) => {
    if (!value || !value.trim()) return "";
    if (!dompurify) return value;
    const rawHtml = marked.parse(value, { mangle: false, headerIds: false });
    const html = typeof rawHtml === "string" ? rawHtml : String(rawHtml);
    return dompurify.sanitize(html, {
      ALLOWED_TAGS: [
        "a", "abbr", "blockquote", "br", "code", "em",
        "h1", "h2", "h3", "h4", "h5", "h6", "hr", "input", "li", "ol", "p", "pre",
        "strong", "sub", "sup", "table", "tbody", "td", "th", "thead", "tr", "ul",
      ],
      ALLOWED_ATTR: [
        "href", "title", "target", "rel", "src", "alt", "width", "height",
        "colspan", "rowspan", "type", "checked", "disabled",
      ],
      ALLOW_DATA_ATTR: false,
      USE_PROFILES: { html: true },
      FORBID_TAGS: ["script", "style", "iframe"],
    });
  };

  const PAGE_SIZE = 30;

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(page * PAGE_SIZE));
      if (search) params.set("search", search);
      if (roleFilter) params.set("role", roleFilter);

      const data = await apiRequest<SessionsResponse>(`/api/admin/conversations?${params}`);
      setSessions(data.sessions);
      setTotal(data.total);
    } catch (err) {
      console.error("Failed to fetch conversations", err);
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter]);

  const fetchMessages = useCallback(async (sessionId: string) => {
    setMessagesLoading(true);
    try {
      const data = await apiRequest<MessagesResponse>(`/api/admin/conversations/${encodeURIComponent(sessionId)}?limit=200`);
      setMessages(data.messages);
    } catch (err) {
      console.error("Failed to fetch messages", err);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleDelete = async () => {
    if (!selectedSession) return;
    const confirmed = window.confirm(
      "Permanently delete this conversation?\n\nThis HARD-deletes every message in the session from the database. It cannot be undone.",
    );
    if (!confirmed) return;
    setDeleting(true);
    try {
      await apiRequest(`/api/admin/conversations/${encodeURIComponent(selectedSession)}`, {
        method: "DELETE",
      });
      setSelectedSession(null);
      setMessages([]);
      await fetchSessions();
    } catch (err) {
      console.error("Failed to delete conversation", err);
      window.alert("Failed to delete the conversation. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    setSearch(searchInput);
  };

  const openSession = (sessionId: string) => {
    setSelectedSession(sessionId);
    fetchMessages(sessionId);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="page wide">
      <PageHeader
        title="Conversations"
        description="Browse all AI assistant conversations across all users."
      />

      {/* Search & Filters */}
      <div className="card" style={{ marginBottom: 14 }}>
        <form className="tbar" onSubmit={handleSearch} style={{ borderBottom: "none" }}>
          <div className="srch" style={{ maxWidth: "none", flex: 1 }}>
            <svg viewBox="0 0 24 24"><use href="#i-search" /></svg>
            <input
              className="inp"
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by user ID, session ID, or message content..."
            />
          </div>
          <select
            className="sel fsel"
            value={roleFilter}
            onChange={(e) => { setRoleFilter(e.target.value); setPage(0); }}
          >
            <option value="">All roles</option>
            <option value="admin">Admin</option>
            <option value="business_owner">Business Owner</option>
            <option value="preferred_member">Preferred Member</option>
            <option value="product_center">Product Center</option>
            <option value="branch_operator">Branch Operator</option>
            <option value="guest">Guest</option>
          </select>
          <Button type="submit" size="sm"><Icon name="search" />Search</Button>
        </form>
      </div>

      {/* Inline columns instead of the `md b` modifier: the single-letter `b`
          class collides with the `.b` badge class and leaked its border/height. */}
      <div className="md" style={{ gridTemplateColumns: "1fr 2fr" }}>
        {/* Sessions List */}
        <div className="card">
          <div className="card-h tight">
            <div>
              <h2>Sessions <span className="b def">{total}</span></h2>
            </div>
          </div>

          <div className="list-scroll" style={{ maxHeight: "calc(100vh - 300px)", minHeight: 480 }}>
            {loading ? (
              <div className="empty sm"><p>Loading...</p></div>
            ) : sessions.length === 0 ? (
              <div className="empty sm"><p>No conversations found.</p></div>
            ) : (
              sessions.map((s, i) => (
                <button
                  key={`${s.session_id}-${i}`}
                  type="button"
                  onClick={() => openSession(s.session_id)}
                  className={`list-i${selectedSession === s.session_id ? " on" : ""}`}
                  style={{ alignItems: "flex-start", padding: 12 }}
                >
                  <span className="tx">
                    <span style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5 }}>
                      <b className="mono" style={{ fontSize: "11.5px", fontWeight: 600 }}>{s.user_id || "guest"}</b>
                      <RoleBadge role={s.user_role || "guest"} />
                    </span>
                    <span
                      style={{
                        fontSize: "12.5px",
                        color: "var(--ink-2)",
                        lineHeight: 1.45,
                        marginBottom: 6,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {s.first_query || "(no messages)"}
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: 9, fontSize: "10.5px", color: "var(--ink-4)", fontWeight: 540 }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} style={{ width: 11, height: 11 }}><use href="#i-msg" /></svg>{s.message_count} messages
                      </span>
                      <span>·</span>
                      <span>
                        {s.last_message_at
                          ? formatDistanceToNow(new Date(s.last_message_at), { addSuffix: true })
                          : ""}
                      </span>
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>

          {/* Pagination */}
          {!loading && sessions.length > 0 && totalPages > 1 && (
            <div className="pg">
              <span className="info">Page <b>{page + 1}</b> of <b>{totalPages}</b></span>
              <div className="r">
                <button
                  className="pgb"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  <svg viewBox="0 0 24 24"><use href="#i-l" /></svg>Previous
                </button>
                <button
                  className="pgb"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next<svg viewBox="0 0 24 24"><use href="#i-r" /></svg>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Message Thread */}
        <div className="card" style={{ display: "flex", flexDirection: "column" }}>
          {selectedSession ? (
            <>
              <div className="card-h">
                <div>
                  <h2>Conversation</h2>
                  <p className="mono" style={{ fontSize: "11.5px" }}>{selectedSession}</p>
                </div>
                <div className="r">
                  <Button variant="danger" size="sm" onClick={handleDelete} loading={deleting} disabled={deleting || messagesLoading}>
                    <Icon name="trash" />Delete
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedSession(null); setMessages([]); }}>
                    <Icon name="x" />Close
                  </Button>
                </div>
              </div>

              {messagesLoading ? (
                <div className="empty"><p>Loading messages...</p></div>
              ) : messages.length === 0 ? (
                <div className="empty"><p>No messages in this conversation.</p></div>
              ) : (
                <div className="thread" style={{ flex: 1, overflowY: "auto", maxHeight: "calc(100vh - 330px)", minHeight: 460 }}>
                  {messages.map((m) => (
                    <div key={m.message_id} style={{ display: "contents" }}>
                      {/* User query */}
                      {m.query && (
                        <div className="bub-w u">
                          <div className="bub u" style={{ whiteSpace: "pre-wrap" }}>{m.query}</div>
                          <div className="bub-m">{m.time ? format(new Date(m.time), "MMM d, h:mm a") : ""}</div>
                        </div>
                      )}

                      {/* Assistant response */}
                      {m.response && (
                        <div className="bub-w">
                          <div className="bub a">
                            <div
                              className="rich"
                              dangerouslySetInnerHTML={{ __html: renderMarkdown(m.response) }}
                            />
                          </div>
                          <div className="bub-m">
                            <span>{m.time ? format(new Date(m.time), "MMM d, h:mm a") : ""}</span>
                            {m.response_time_ms > 0 && (
                              <span>{(m.response_time_ms / 1000).toFixed(1)}s</span>
                            )}
                            {m.token_used > 0 && (
                              <span>{m.token_used} tokens</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="empty">
              <div className="empty-i"><Icon name="chat" /></div>
              <b>Select a conversation</b>
              <p>Click a session from the list to view the full conversation thread.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Conversations;
